/** @prettier */

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';
import {useState, useEffect} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import {ChkInputTextbox} from '../../component/ChkInputTextbox';
import TextFieldSelectBox, {
  SLIUAddressSelectBox,
} from '../../component/SelectBox';
import SLIFUnitTypeSelectBox from './SLIFUnitTypeSelectBox';
import APIErrorMessage from '../../component/APIErrorMessage';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
//API import
import {
  getCompositionsSLIFUnit,
  putCompositionsSLIFUnit,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';

import {sortByUnicode} from '../../functions/sort';
import {format, limit, pullDownMenu} from '../../define';

/**
 * その他空調機編集ダイアログ
 * @param props props
 * @returns JSX.Element
 */
const OtherEditDialog = (props: {
  /** 修正する空調機ID */
  unitID: string;
  /** 修正する空調機の情報 */
  displayUnitInfo: apiIF.resParameters.slIFUnitInformations;
  /** 修正する空調機の情報（set関数） */
  setUnitInfo: React.Dispatch<
    React.SetStateAction<apiIF.res.getCompositionsSLIFUnit | null>
  >;
  /** ダイアログを開くかどうか */
  isOpenDialog: boolean;
  /** ダイアログを開くかどうか（set関数） */
  setIsOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  /** 課金しているかどうか */
  isBilling: boolean;
  /** GW毎の構成情報未反映の有無（set関数） */
  setHasConfigGW: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const trans = useTranslation().t; //翻訳関数
  //パスパラメータ取得
  const {siteID, gwID} = useParams();

  //編集前のその他空調機情報を取得
  const tempOtherName =
    props.displayUnitInfo['unit-name'] === undefined
      ? ''
      : props.displayUnitInfo['unit-name'];
  const tempSystem =
    props.displayUnitInfo['sl-system'] === undefined
      ? ''
      : props.displayUnitInfo['sl-system'];
  const tempOtherAddress =
    props.displayUnitInfo['sl-address'] === undefined
      ? ''
      : props.displayUnitInfo['sl-address'];
  const tempUnitType =
    props.displayUnitInfo['unit-type'] === undefined
      ? 'FDT'
      : props.displayUnitInfo['unit-type'];
  const tempIFType =
    props.displayUnitInfo['interface-type'] === undefined
      ? 'SC-ADNA'
      : props.displayUnitInfo['interface-type'];
  const tempPowerSupply =
    props.displayUnitInfo['power-supply'] === undefined
      ? 'threePhase'
      : props.displayUnitInfo['power-supply'];
  const tempVoltage =
    props.displayUnitInfo['input-voltage'] === undefined
      ? '0'
      : props.displayUnitInfo['input-voltage'];

  const [otherName, setOtherName] = useState(tempOtherName); //空調機の名前
  const [slSystem] = useState(tempSystem); //系統番号
  const [otherAddress, setOtherAddress] = useState(tempOtherAddress); //SLアドレス
  const [unitType, setUnitType] = useState(tempUnitType); //空調機タイプ
  const [ifType, setIFType] = useState(tempIFType); //インターフェイスタイプ
  const [otherPowerSupply, setOtherPowerSupply] = useState(tempPowerSupply); //電源の仕様
  const [otherVoltage, setOtherVoltage] = useState(tempVoltage); //入力電圧
  const [okBtnDisabled, setOKBtnDisabled] = useState(false); //OKボタン非表示フラグ
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  //TextBoxのエラー状態
  const [otherNameError, setOtherNameError] = useState(false); //空調機の名前
  const [otherVoltageError, setOtherVoltageError] = useState(false); //入力電圧

  //SLアドレスの設定項目
  const slOtherAddressCurrencies = [];
  for (let i = 0; i < limit.REGISTRATION_NUM_NEW_SL_IU_MAX; i++) {
    slOtherAddressCurrencies.push({value: String(i), label: String(i)});
  }

  //入力項目のチェック
  useEffect(() => {
    if (
      otherName === '' ||
      otherVoltage === '' ||
      otherNameError ||
      apiWait ||
      otherVoltageError
    ) {
      setOKBtnDisabled(true);
    } else {
      setOKBtnDisabled(false);
    }
  }, [
    otherName,
    unitType,
    otherVoltage,
    otherNameError,
    otherVoltageError,
    apiWait,
  ]);

  /**
   * OKボタン押下時処理
   * @returns
   */
  const editUnit = () => {
    //パスパラメータを取得できない場合
    if (
      siteID === undefined ||
      gwID === undefined ||
      (slSystem !== '1' && slSystem !== '2')
    ) {
      return;
    }

    setAPIWait(true);

    //情報を修正
    const editData: apiIF.reqParameters.putCompositionsSLIFUnit = {
      'unit-name': otherName,
      'sl-system': slSystem,
      'sl-address': otherAddress,
      'unit-type': unitType,
      'interface-type': ifType,
      'power-supply': otherPowerSupply,
      'input-voltage': otherVoltage,
    };
    putCompositionsSLIFUnit(siteID, gwID, props.unitID, editData)
      .then(response => {
        //情報を取得
        return getCompositionsSLIFUnit(siteID, gwID, {'max-result': 400});
      })
      .then(response => {
        //表示内容をUnicode順に並び替え
        response['unit-informations'].sort(sortByUnicode('unit-name'));

        props.setUnitInfo(() => response);
        props.setIsOpenDialog(false);
        props.setHasConfigGW(props.isBilling); //課金している場合のみ表示
      })
      .catch(() => {
        setIsOpenErrorDialog(true);
      })
      .finally(() => {
        setAPIWait(false);
      });
  };
  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    props.setIsOpenDialog(false);
    setOtherName(tempOtherName);
    setOtherAddress(tempOtherAddress);
    setUnitType(tempUnitType);
    setIFType(tempIFType);
    setOtherVoltage(tempVoltage);
    setOtherPowerSupply(tempPowerSupply);
  };

  return (
    <>
      {/* 歯車ボタン内に編集ボタンを表示 */}
      <Dialog
        open={props.isOpenDialog}
        onClose={handleClose}
        aria-labelledby="unit-edit-dialog-title">
        <DialogTitle id="unit-edit-dialog-title">
          {trans('OtherEditDialog.title')}
        </DialogTitle>
        <DialogContent>
          {/* ダイアログに表示するメッセージ */}
          <DialogContentText>{trans('OtherEditDialog.msg')}</DialogContentText>
          <Stack spacing={1.5}>
            {/* 新しい室内機名称 */}
            <ChkInputTextbox
              setValue={setOtherName}
              setError={setOtherNameError}
              chkLength={format.BASIC_LENGTH_MAX}
              type="text"
              label={trans('OtherEditDialog.txtField.label.interfaceName')}
              value={otherName}
              id="mv-editIFName"
              class="gwSetting__contents__textBox--unitName"
              helperText=" "
              errHelperText={trans('OtherEditDialog.txtField.errHelperTxt')}
              variant="standard"
              autoFocus={true}
              margin="dense"
              chkInputProhibitedCharacter={format.BASIC_PATTERN_S3_POLICY}
            />
            {/* 室内機アドレスの選択 */}
            <SLIUAddressSelectBox
              setAddress={setOtherAddress}
              label={trans('OtherEditDialog.txtField.label.superlinkAddress')}
              default={tempOtherAddress}
              class="gwSetting__contents__textBox--address"
              id="mv-editSelectIFUnit"
            />
            {/* 空調機タイプ */}
            <SLIFUnitTypeSelectBox
              setUnitType={setUnitType}
              defaultValue={unitType}
            />
            {/* インターフェイスタイプの選択 */}
            <TextFieldSelectBox
              label={trans('OtherEditDialog.txtField.label.interfaceType')}
              choices={pullDownMenu.INTERFACE_TYPE}
              setValue={setIFType}
              default={ifType}
              id="mv-otherEditIntfTypeSelect"
              class="gwSetting__contents__selectBox--interface"
            />
            {/* 電圧の入力 */}
            <ChkInputTextbox
              setValue={setOtherVoltage}
              setError={setOtherVoltageError}
              chkInputTxtOK={format.VOLTAGE_PATTERN_OK}
              type="text"
              label={trans('OtherEditDialog.txtField.label.voltage')}
              value={otherVoltage}
              id="mv-editIFVoltageInput"
              class="gwSetting__contents__textBox--voltage"
              helperText=" "
              errHelperText={trans('OtherEditDialog.txtField.errHelperTxt')}
              variant="standard"
            />
            {/* 電源の種類 */}
            <TextFieldSelectBox
              label={trans('OtherEditDialog.txtField.label.powerSupply')}
              choices={pullDownMenu.POWER_SUPPLY_TYPE}
              setValue={setOtherPowerSupply}
              default={otherPowerSupply}
              id="mv-editIFPhaseSelect"
              class="gwSetting__contents__selectBox--powerSupply"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            disabled={apiWait}
            onClick={handleClose}
            buttonText={trans('OtherEditDialog.buttonTxt.cancel')}
            id={'mv-otherEditCancelBtn'}
          />
          <PrimaryButton
            onClick={editUnit}
            buttonText={trans('OtherEditDialog.buttonTxt.ok')}
            id={'mv-otherEditOkBtn'}
            disabled={okBtnDisabled}
            apiWait={apiWait}
          />
        </DialogActions>
      </Dialog>
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
        id={`${tempOtherName}EditErrDlgBtn`}
      />
    </>
  );
};
export default OtherEditDialog;
